/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { useEffect, useMemo, useState } from 'react';

import { generateGuid } from '../../helpers/string-helpers';

const historyStack: string[] = [];

const lastItem = (arr: unknown[]) => arr[arr.length - 1];

export const usePopstate = (onPopState: () => void, initialActiveState: boolean) => {
    const [activeState, setActiveState] = useState(initialActiveState);

    const [id] = useMemo(() => generateGuid(), []);

    const activate = () => {
        setActiveState(true);

        historyStack.push(id);
        window.history.pushState({ modalID: id }, '');
    };

    const deactivate = (event?: PopStateEvent) => {
        if (activeState === false || historyStack.length === 0) {
            return;
        }

        // Restore previous history state in case of closing modal by app
        if (!event) {
            historyStack.pop();
            setActiveState(false);

            window.history.back();
            return;
        }

        // Some other modal has been closed, nothing to do
        if (event.state && event.state.modalID && event.state.modalID === id) {
            return;
        }

        // Browser's button forward has been clicked, nothing to
        if (event.state && event.state.modalID && !historyStack.includes(event.state.modalID)) {
            return;
        }

        // Close modal by navigating back on browser's history
        if (lastItem(historyStack) === id) {
            historyStack.pop();
            setActiveState(false);

            onPopState();
            return;
        }
    };

    useEffect(
        () => {
            window.addEventListener('popstate', deactivate);

            return () => window.removeEventListener('popstate', deactivate);
        },
        [id, activeState]
    );

    return {
        activate,
        deactivate,
    };

};
